import {
  MappingEditorProps,
  RecursiveQuestionMappingItemProps,
  RecursiveQuestionsColumnMapperProps
} from "@feature/services/associate-services-logic/types";
import {FormattedMessage} from "react-intl";
import SearchSelect from "@ui-components/SearchSelect";
import {
  QuotationSurveyRecursiveCopyMappingRuleSchema,
  QuotationSurveyRecursiveCopySettingsSchema
} from "@feature/types";
import {ArrowRightIcon, LinkIcon} from "@heroicons/react/24/outline";
import Button from "@ui-components/Button";
import React, {useState} from "react";
import {ArrowLongLeftIcon} from "@heroicons/react/24/solid";
import _ from "lodash";


const MappingEditor = ({
  index,
  label,
  options,
  value,
  onChange,
  disabled = false,
  nested = false,
}: MappingEditorProps) => {

  // utilities
  const selected = options
    .find(({value: optionValue}) => optionValue === value);
  const MiddleIcon = nested ? ArrowRightIcon : LinkIcon;

  return <>
    <div className="flex flex-col gap-0.5">
      {
        !nested || index === undefined
          ? label
          : (
            <div className="flex flex-row border">
              <div className="border-r w-16 p-2 flex items-center justify-center">
                OPT#{index+1}
              </div>
              <div className="p-2 flex items-center justify-center">
                {label}
              </div>
            </div>
          )
      }
    </div>
    <div className="ml-2 mr-3">
      <MiddleIcon className="w-4" />
    </div>
    <div className="mt-1.5">
      {/*@ts-ignore*/}
      <SearchSelect
        key={selected?.toString()}  // force re-render on parent question value change
        options={options}
        value={selected}
        onChange={(selected: {value: number} | undefined) => { onChange(selected?.value); }}
        isClearable={true}
        isDisabled={disabled}
      />
    </div>
  </>
}


const RecursiveQuestionMappingItem = ({
  localQuestion,
  localQuestionOptions,
  designServiceConfigurations,
  designRecursiveQuestionId,
  value,
  onChange,
}: RecursiveQuestionMappingItemProps) => {

  // utilities
  const designQuestionOptions = designServiceConfigurations
    .filter(({type, input_type, recursive_question_id}) =>
      type === "question"
      && recursive_question_id === designRecursiveQuestionId
      && input_type === localQuestion.input_type
    )
    .map(({id, name}) => ({value: id, label: name || ""}));
  const designQuestionOptionsOptions = designServiceConfigurations
    .filter(({type, input_id}) =>
      type === "option"
      && input_id === value?.id_src_question
    )
    .map(({id, name}) => ({value: id, label: name || ""}));
  const mustMapOptions =
    ["select", "multi_select"].includes(localQuestion.input_type || "");
  
  return <>
    {/* -------------------- Question mapping -------------------- */}
    <MappingEditor
      label={localQuestion.name || ""}
      options={designQuestionOptions}
      value={value?.id_src_question}
      onChange={(designQuestionId) => {
        onChange({
          id_dst_question: localQuestion.id,
          id_src_question: designQuestionId,
          options_mapping: undefined,
        } as QuotationSurveyRecursiveCopyMappingRuleSchema);
      }}
    />
    {/* -------------------- Options mapping -------------------- */}
    {mustMapOptions && <>
      <div className="col-span-3 text-xs font-medium">
        <FormattedMessage id="options_mapping" defaultMessage="Abbina opzioni" />
      </div>
      {localQuestionOptions?.map((lqo, idx) => (
        <MappingEditor
          key={idx}
          index={idx}
          label={lqo.name || ""}
          options={designQuestionOptionsOptions}
          value={value?.options_mapping?.[lqo.id]}
          onChange={(selected) => {
            // update options mapping
            const om = value?.options_mapping || {};
            if (!selected)
              delete om[lqo.id];
            else
              om[lqo.id] = selected;
            // save changes to mapping rule
            onChange({
              ...value,
              options_mapping: Object.keys(om).length > 0 ? om : undefined,
            } as QuotationSurveyRecursiveCopyMappingRuleSchema);
          }}
          disabled={!value?.id_dst_question}
          nested
        />
      ))}
    </>}
  </>
}

export const RecursiveQuestionsColumnMapper = ({
  mapping,
  innerQuestions,
  designServices,
  serviceConfigurations,
  onClose,
  onSave,
}: RecursiveQuestionsColumnMapperProps) => {

  // state
  const [localMapping, setLocalMapping]
    = useState<QuotationSurveyRecursiveCopySettingsSchema>(
      mapping
      || {} as QuotationSurveyRecursiveCopySettingsSchema
    );

  // utilities
  const designServicesOptions = designServices
    .map(({id, name}) => ({value: id, label: name}));
  const selectedDesignService = designServices
    .find(({id}) => id === localMapping?.id_design_service);
  const selectedDesignServiceRecursiveQuestionsOptions = selectedDesignService
    ?.configurations
    .filter(({type}) => type === "recursive_question")
    .map(({id, name}) => ({value: id, label: name}))
    ?? [];
  const selectedRecursiveQuestion = selectedDesignService
    ?.configurations
    .find(({id}) => id === localMapping?.id_source_recursive_question);
  const updateMappingRule = (rule: QuotationSurveyRecursiveCopyMappingRuleSchema, index?: number) => {
    // update mapping rule
    const qm = localMapping?.questions_mapping || [];
    if (index === undefined)
      qm.push(rule);
    else if (!!rule.id_src_question)
      qm[index] = rule
    else
      qm.splice(index, 1);
    // save changes to logic
    setLocalMapping((prev) => ({
      ...prev,
      questions_mapping: qm,
    }));
  }
  const validate = (m: QuotationSurveyRecursiveCopySettingsSchema) => (
    // 1. design service must be selected
    !!m.id_design_service
    // 2. source recursive question must be selected
    && !!m.id_source_recursive_question
    // 3. all inner questions must be mapped
    && innerQuestions
      .every(({id: iqid}) =>
        m.questions_mapping?.find(({id_dst_question: qmid}) => qmid === iqid)
      )
    // 4. all options of select/multi-select inner questions must be mapped
    && innerQuestions
      .filter(({input_type}) => ["select", "multi_select"].includes(input_type || ""))
      .every(({id}) => {
        const options = serviceConfigurations
          .filter(({type, input_id}) =>
            type === "option"
            && input_id === id
          );
        return options
          .every(({id: oid}) =>
            m.questions_mapping
              ?.find(({id_dst_question, options_mapping}) =>
                id_dst_question === id
                && !!options_mapping?.[oid]
              )
          )
      })
  )

  if (!innerQuestions.length)
    return (
      <div className="text-center italic text-xs p-6 bg-gray-50 border border-dashed">
        <FormattedMessage
          id="no_questions_to_map"
          defaultMessage="Nessuna domanda da associare"
        />
      </div>
    );

  return (
    <div className="h-full flex flex-col justify-between">

      {/* ******************** TOP SECTION ******************** */}
      <div>
        <div className="flex flex-col gap-2">

          {/* ==================== Tab title + back button ==================== */}
          <div className="w-full flex mt-2 mb-4">
            <div className="flex flex-row gap-3 items-center">
              <ArrowLongLeftIcon
                className="cursor-pointer w-10"
                onClick={() => {
                  onClose(_.isEqual(mapping, localMapping));
                }}
              />
              <h3 className="text-xl font-semibold">
                <FormattedMessage id={!!mapping ? "edit_logic" : "add_logic"} />
              </h3>
            </div>
          </div>

          {/* ==================== Configuration ==================== */}
          {/* -------------------- Design service selector -------------------- */}
          <div>
            <FormattedMessage
              id="logic_target_design_service"
              defaultMessage="Progettazione di riferimento"
            />
            {/*@ts-ignore*/}
            <SearchSelect
              options={designServicesOptions}
              value={designServicesOptions.find(({value}) =>
                value === localMapping?.id_design_service)
              }
              onChange={({value}: {value: number}) => {
                setLocalMapping((prev) => ({
                  ...prev,
                  id_design_service: value,
                }));
              }}
            />
          </div>

          {/* -------------------- Mappings -------------------- */}
          {!!localMapping.id_design_service && <>
            <div>
              <FormattedMessage
                id="logic_target_design_service_recuersive_question"
                defaultMessage="Domanda ricorsiva di riferimento"
              />
              {/*@ts-ignore*/}
              <SearchSelect
                options={selectedDesignServiceRecursiveQuestionsOptions}
                value={selectedDesignServiceRecursiveQuestionsOptions
                  .find(({value}) => value === localMapping?.id_source_recursive_question)
                }
                onChange={({value}: {value: number}) => {
                  setLocalMapping((prev) => ({
                    ...prev,
                    id_source_recursive_question: value,
                  }));
                }}
              />
            </div>

            {!!localMapping.id_source_recursive_question && (
              <div className="grid gap-x-3 items-center" style={{gridTemplateColumns: "1fr min-content 1fr"}}>
                <div className="font-medium text-xs mb-2">
                  <FormattedMessage id="quotation_question" defaultMessage="Domanda quotazione" />
                </div>
                <div />
                <div className="font-medium text-xs mb-2">
                  <FormattedMessage id="design_question" defaultMessage="Domanda progettazione" />
                </div>
                {innerQuestions.map((iq, idx) =>
                  <>
                    <RecursiveQuestionMappingItem
                      key = {idx}
                      localQuestion = {iq}
                      localQuestionOptions = {serviceConfigurations.filter(({type, input_id}) =>
                        type === "option"
                        && input_id === iq.id
                      )}
                      designServiceConfigurations = {selectedDesignService?.configurations || []}
                      designRecursiveQuestionId={selectedRecursiveQuestion?.id!}
                      value={
                        localMapping.questions_mapping
                          ?.find(({id_dst_question}) => id_dst_question === iq.id)
                      }
                      onChange={(mr) => {
                        const ruleIndex = localMapping.questions_mapping
                          ?.findIndex(({id_dst_question}) => id_dst_question === iq.id);
                        updateMappingRule(mr, ruleIndex !== undefined && ruleIndex >= 0 ? ruleIndex : undefined);
                      }}
                    />
                    <div className="col-span-3 border-b-2 border-gray-100 border-dashed my-2" />
                  </>
                )}
              </div>
            )}
          </>}

        </div>
      </div>

      {/* ******************** BOTTOM SECTION ******************** */}
      <div className="flex flex-row justify-end">
        {/* ==================== Save button ==================== */}
        {/*@ts-ignore*/}
        <Button
          styleType="darkGreen"
          onClick={() => { onSave(localMapping); }}
          disabled={!validate(localMapping)}
        >
          <FormattedMessage id="confirm" />
        </Button>
      </div>

    </div>
  )
}